import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Mobile-friendly attendance photo: larger tap target, full-screen lightbox
 * (avoids relying on "open in new tab" which is unreliable on some mobile browsers).
 */
export default function AttendancePhotoThumb({
  src,
  caption,
  altText = 'Attendance verification photo'
}) {
  const [open, setOpen] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);
  const [fullBroken, setFullBroken] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    setThumbBroken(false);
    setFullBroken(false);
  }, [src]);

  if (!src || thumbBroken) {
    return <span className="text-gray-400 text-sm">—</span>;
  }

  const modal =
    open &&
    createPortal(
      <div
        className="fixed inset-0 z-[200] flex flex-col bg-black/92 touch-manipulation overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
        role="dialog"
        aria-modal="true"
        aria-label="Full size attendance photo"
      >
        <div
          className="flex shrink-0 justify-end p-2"
          style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex h-11 min-w-11 items-center justify-center rounded-full bg-white/15 text-2xl leading-none text-white hover:bg-white/25 active:bg-white/30"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          className="min-h-0 flex flex-1 flex-col items-center justify-center overflow-auto px-3"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          onClick={(e) => e.stopPropagation()}
        >
          {!fullBroken ? (
            <img
              src={src}
              alt={altText}
              className="max-h-[min(85dvh,85vh)] w-auto max-w-full rounded object-contain shadow-xl"
              loading="eager"
              decoding="async"
              draggable={false}
              onError={() => setFullBroken(true)}
            />
          ) : (
            <p className="text-center text-white/80 text-sm px-4">Could not load this image.</p>
          )}
          {caption ? (
            <p className="mt-3 max-w-lg text-center text-sm text-white/90">{caption}</p>
          ) : null}
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 text-sm text-indigo-300 underline underline-offset-2"
            onClick={(e) => e.stopPropagation()}
          >
            Open in browser tab
          </a>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex max-w-full flex-col items-stretch gap-1 rounded-xl border border-gray-200 bg-gray-50/80 p-2 text-left touch-manipulation shadow-sm active:bg-gray-100 sm:inline-flex sm:flex-row sm:items-center sm:gap-2 sm:p-1.5"
        aria-label={`View ${altText} full screen`}
      >
        <img
          src={src}
          alt=""
          className="mx-auto h-24 w-24 shrink-0 rounded-lg object-cover sm:mx-0 sm:h-14 sm:w-14"
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setThumbBroken(true)}
        />
        <span className="text-center text-xs font-medium text-indigo-700 sm:text-left sm:text-indigo-600">
          <span className="sm:hidden">Tap photo to view full size</span>
          <span className="hidden sm:inline">Enlarge</span>
        </span>
      </button>
      {modal}
    </>
  );
}
